import { ApiError, apiErrorFromStatus } from './errors';

export interface ApiClientOptions {
  baseUrl: string;
  getAuthToken?: () => Promise<string | null> | string | null;
  fetchImpl?: typeof fetch;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getAuthToken?: ApiClientOptions['getAuthToken'];
  private readonly fetchImpl: typeof fetch;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.getAuthToken = options.getAuthToken;
    this.fetchImpl = options.fetchImpl || fetch.bind(globalThis);
  }

  async get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>('GET', path, undefined, signal);
  }

  async post<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>('POST', path, body, signal);
  }

  async patch<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>('PATCH', path, body, signal);
  }

  private async request<T>(
    method: 'GET' | 'POST' | 'PATCH',
    path: string,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<T> {
    const headers = new Headers({ Accept: 'application/json' });
    if (body !== undefined) headers.set('Content-Type', 'application/json');

    const token = this.getAuthToken ? await this.getAuthToken() : null;
    if (token) headers.set('Authorization', `Bearer ${token}`);

    let response: Response;
    try {
      response = await this.fetchImpl(this.url(path), {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new ApiError('Network request failed', 'network');
    }

    if (!response.ok) {
      let message: string | undefined;
      let backendCode: string | undefined;
      try {
        const payload = (await response.json()) as { message?: string; error?: string };
        backendCode = payload.error;
        message = payload.message || payload.error;
      } catch {
        message = response.statusText;
      }
      throw apiErrorFromStatus(response.status, message, backendCode);
    }

    if (response.status === 204) return undefined as T;

    const contentType = response.headers.get('Content-Type') || '';
    if (!contentType.includes('application/json')) {
      throw new ApiError('Expected JSON response from API', 'unknown', response.status);
    }

    try {
      return (await response.json()) as T;
    } catch {
      throw new ApiError('Invalid JSON response from API', 'unknown', response.status);
    }
  }

  private url(path: string): string {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.baseUrl}${cleanPath}`;
  }
}
